import React, { useState, useRef, useEffect, useMemo } from 'react';
import { jsxs, jsx } from 'react/jsx-runtime';

// src/components/pitch-gauge.tsx
var mapCentsToPercent = (cents) => {
  const clampedCents = Math.max(-50, Math.min(50, cents));
  return (clampedCents + 50) / 100;
};
var smoothValue = (current, target, alpha = 0.3) => {
  return current + (target - current) * alpha;
};
var FLAT_COLOR = "#ef4444";
var INTUNE_COLOR = "#10b981";
var SHARP_COLOR = "#f59e0b";
var polarToCartesian = (cx, cy, r, angleDeg) => {
  const rad = angleDeg * Math.PI / 180;
  return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
};
var describeArcBand = (cx, cy, rOuter, rInner, startAngle, endAngle) => {
  const outerStart = polarToCartesian(cx, cy, rOuter, startAngle);
  const outerEnd = polarToCartesian(cx, cy, rOuter, endAngle);
  const innerStart = polarToCartesian(cx, cy, rInner, endAngle);
  const innerEnd = polarToCartesian(cx, cy, rInner, startAngle);
  const largeArc = Math.abs(endAngle - startAngle) > 180 ? 1 : 0;
  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 0 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerStart.x} ${innerStart.y}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 1 ${innerEnd.x} ${innerEnd.y}`,
    "Z"
  ].join(" ");
};
var GaugeSVG = React.memo(({ percent }) => {
  const cx = 100, cy = 100, rOuter = 85, rInner = 68;
  const flatBand = describeArcBand(cx, cy, rOuter, rInner, 121, 179);
  const inTuneBand = describeArcBand(cx, cy, rOuter, rInner, 61, 119);
  const sharpBand = describeArcBand(cx, cy, rOuter, rInner, 1, 59);
  const trackBand = describeArcBand(cx, cy, rOuter + 1, rInner - 1, 0, 180);
  const needleAngle = 180 - percent * 180;
  const needleLen = rInner - 4;
  const needleTip = polarToCartesian(cx, cy, needleLen, needleAngle);
  const needleBase1 = polarToCartesian(cx, cy, 5, needleAngle + 90);
  const needleBase2 = polarToCartesian(cx, cy, 5, needleAngle - 90);
  return /* @__PURE__ */ jsxs("svg", { viewBox: "0 0 200 115", className: "w-full h-full", children: [
    /* @__PURE__ */ jsx("path", { d: trackBand, fill: "var(--gauge-track, #e5e7eb)", opacity: 0.3 }),
    /* @__PURE__ */ jsx("path", { d: flatBand, fill: FLAT_COLOR, opacity: 0.75 }),
    /* @__PURE__ */ jsx("path", { d: inTuneBand, fill: INTUNE_COLOR, opacity: 0.75 }),
    /* @__PURE__ */ jsx("path", { d: sharpBand, fill: SHARP_COLOR, opacity: 0.75 }),
    (() => {
      const inner = polarToCartesian(cx, cy, rInner - 5, 90);
      const outer = polarToCartesian(cx, cy, rOuter + 5, 90);
      return /* @__PURE__ */ jsx(
        "line",
        {
          x1: inner.x,
          y1: inner.y,
          x2: outer.x,
          y2: outer.y,
          stroke: "var(--gauge-tick, #4b5563)",
          strokeWidth: 2.5
        }
      );
    })(),
    [45, 135].map((angle) => {
      const inner = polarToCartesian(cx, cy, rInner - 3, angle);
      const outer = polarToCartesian(cx, cy, rOuter + 3, angle);
      return /* @__PURE__ */ jsx(
        "line",
        {
          x1: inner.x,
          y1: inner.y,
          x2: outer.x,
          y2: outer.y,
          stroke: "var(--gauge-tick-minor, #6b7280)",
          strokeWidth: 1.5
        },
        angle
      );
    }),
    /* @__PURE__ */ jsx("text", { x: "22", y: cy + 14, fontSize: "10", fill: "var(--gauge-label, #4b5563)", fontWeight: "500", textAnchor: "middle", children: "\u266D" }),
    /* @__PURE__ */ jsx("text", { x: "178", y: cy + 14, fontSize: "10", fill: "var(--gauge-label, #4b5563)", fontWeight: "500", textAnchor: "middle", children: "\u266F" }),
    /* @__PURE__ */ jsx(
      "polygon",
      {
        points: `${needleTip.x},${needleTip.y} ${needleBase1.x},${needleBase1.y} ${needleBase2.x},${needleBase2.y}`,
        fill: "var(--gauge-needle, #374151)"
      }
    ),
    /* @__PURE__ */ jsx("circle", { cx, cy, r: 6, fill: "var(--gauge-needle, #374151)" }),
    /* @__PURE__ */ jsx("circle", { cx, cy, r: 3, fill: "var(--gauge-tick-minor, #6b7280)" })
  ] });
});
var PitchGaugeComponent = ({
  note,
  cents,
  className = "",
  clarity,
  inTuneThreshold = 5,
  smoothingFactor = 0.7,
  subdueNote = false
}) => {
  const [smoothedCents, setSmoothedCents] = useState(cents);
  const animationFrameRef = useRef(null);
  useEffect(() => {
    let isAnimating = true;
    const updateSmoothedValue = () => {
      if (!isAnimating) return;
      setSmoothedCents((prevSmoothed) => {
        const baseAlpha = 0.05 + (1 - smoothingFactor) * 0.75;
        const diff = Math.abs(cents - prevSmoothed);
        const adaptiveFactor = diff > 10 ? 1.5 : diff > 5 ? 1 : 0.6;
        const alpha = Math.min(1, baseAlpha * adaptiveFactor);
        const newValue = smoothValue(prevSmoothed, cents, alpha);
        if (Math.abs(newValue - cents) < 0.5) {
          return cents;
        }
        return newValue;
      });
      if (isAnimating) {
        animationFrameRef.current = requestAnimationFrame(updateSmoothedValue);
      }
    };
    animationFrameRef.current = requestAnimationFrame(updateSmoothedValue);
    return () => {
      isAnimating = false;
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [cents, smoothingFactor]);
  const percent = useMemo(() => mapCentsToPercent(smoothedCents), [smoothedCents]);
  const displayCents = useMemo(() => Math.round(smoothedCents), [smoothedCents]);
  const direction = useMemo(
    () => displayCents < -inTuneThreshold ? "\u266D" : displayCents > inTuneThreshold ? "\u266F" : "",
    [displayCents, inTuneThreshold]
  );
  const inTune = Math.abs(displayCents) <= inTuneThreshold;
  return /* @__PURE__ */ jsxs("div", { className: `flex flex-col items-center ${className}`, children: [
    /* @__PURE__ */ jsx("span", { className: `font-bold drop-shadow-md transition-all duration-200 mb-1 ${subdueNote ? "text-2xl opacity-60" : "text-4xl"} ${inTune ? "text-green-600 dark:text-green-400" : "text-gray-800 dark:text-gray-200"}`, children: note }),
    /* @__PURE__ */ jsx("div", { className: "w-56", children: /* @__PURE__ */ jsx(GaugeSVG, { percent }) }),
    /* @__PURE__ */ jsxs("div", { className: "text-center mt-1", children: [
      /* @__PURE__ */ jsxs("span", { className: `text-2xl font-mono font-semibold transition-colors duration-200 ${Math.abs(displayCents) <= inTuneThreshold ? "text-green-600 dark:text-green-400" : displayCents < 0 ? "text-red-500 dark:text-red-400" : "text-amber-500 dark:text-amber-400"}`, children: [
        direction && /* @__PURE__ */ jsx("span", { className: "mr-1", children: direction }),
        Math.abs(displayCents),
        "\xA2"
      ] }),
      /* @__PURE__ */ jsx("div", { className: "text-sm text-muted-foreground mt-0.5", children: inTune ? "In tune! \u2713" : displayCents < 0 ? "Too flat" : "Too sharp" }),
      clarity !== void 0 && /* @__PURE__ */ jsxs("div", { className: "text-xs font-medium text-gray-600 dark:text-gray-300 mt-0.5", children: [
        "clarity: ",
        Math.round(clarity * 100),
        "%"
      ] })
    ] })
  ] });
};
var PitchGauge = React.memo(PitchGaugeComponent, (prevProps, nextProps) => {
  return prevProps.note === nextProps.note && Math.abs(prevProps.cents - nextProps.cents) < 0.5 && prevProps.clarity === nextProps.clarity && prevProps.className === nextProps.className && prevProps.inTuneThreshold === nextProps.inTuneThreshold && prevProps.smoothingFactor === nextProps.smoothingFactor && prevProps.subdueNote === nextProps.subdueNote;
});

export { PitchGauge };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map